import { memo, FC, useState, useCallback, useMemo } from 'react';

import { MassMessageRolesGroup, MassMessageRole } from 'models';

import keyBy from 'lodash/keyBy';
import has from 'lodash/has';

import { Collapse, Button } from '@material-ui/core';
import { Checkbox } from 'components';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import RolesList from '../roles-list';

import clsx from 'clsx';
import { useStyles } from './style';
import { useStyles as useCommonStyles } from '../style';

interface RolesGroupProps {
  group: MassMessageRolesGroup;
  selectedRoles: MassMessageRole[];
  onSelectRoles?: (roles: MassMessageRole[], checked: boolean) => void;
  disabled: boolean;
}

const RolesGroup: FC<RolesGroupProps> = ({
  group,
  selectedRoles,
  onSelectRoles,
  disabled,
}) => {
  const rolesGroupClasses = useStyles();
  const commonClasses = useCommonStyles();

  const [expandedRolesGroup, setExpandedRolesGroup] = useState(false);

  const hasRoles = !!group.Roles.length;
  const canExpand = hasRoles && group.CanExpand;
  const selectedRolesDict = useMemo(
    () => keyBy(selectedRoles, 'Id'),
    [selectedRoles]
  );
  const selectedAll = useMemo(() => {
    return hasRoles
      ? group.Roles.every((role) => has(selectedRolesDict, role.Id))
      : false;
  }, [hasRoles, group, selectedRolesDict]);
  const selectedAny = useMemo(() => {
    return group.Roles.some((role) => has(selectedRolesDict, role.Id));
  }, [group.Roles, selectedRolesDict]);
  const indeterminate = selectedAny && !selectedAll;

  const handleExpandRolesGroup = useCallback(() => {
    setExpandedRolesGroup((prevState) => !prevState);
  }, []);

  const handleSelectAll = useCallback(() => {
    if (onSelectRoles) {
      onSelectRoles([...group.Roles], indeterminate ? false : !selectedAll);
    }
  }, [group.Roles, selectedAll, onSelectRoles, indeterminate]);

  return (
    <li className={commonClasses.li}>
      <div className={commonClasses.bar}>
        {canExpand ? (
          <Button
            className={clsx(rolesGroupClasses.button, {
              [rolesGroupClasses.buttonActive]: expandedRolesGroup,
            })}
            onClick={handleExpandRolesGroup}
          >
            <ChevronRightIcon className={rolesGroupClasses.chevronIcon} />
          </Button>
        ) : (
          <div className={commonClasses.mock}></div>
        )}
        <Checkbox
          disabled={disabled}
          checked={selectedAll}
          indeterminate={indeterminate}
          onChange={handleSelectAll}
        />
        <span className={commonClasses.text}>{group.Name}</span>
      </div>
      <Collapse in={expandedRolesGroup}>
        <RolesList
          disabled={disabled}
          roles={group.Roles}
          selectedRoles={selectedRoles}
          onSelectRoles={onSelectRoles}
        />
      </Collapse>
    </li>
  );
};

export default memo(RolesGroup);
